// src/services/anonymizationRunsService.js

const API_BASE_URL = process.env.REACT_APP_API_ADDRESS;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  'Content-Type': 'application/json',
});

// adjust this to your router prefix if different
const BASE_PATH = `${API_BASE_URL}/anonymization/runs`;

export const RUN_PROCESS_TYPES = {
  ANONYMIZATION: 'anonymization',
  PSEUDONYMIZATION: 'pseudonymization',
  DEPSEUDONYMIZATION: 'depseudonymization',
  VALIDATION: 'validation',
};

export const RUN_STATUSES = {
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
};

/**
 * Helper: build URL with optional query params
 */
const buildUrl = (path, params = {}) => {
  const url = new URL(path);

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    url.searchParams.set(key, String(value));
  });

  return url;
};

/**
 * List anonymization runs (ReadOnlyModelViewSet list)
 *
 * Filters supported by your backend:
 *  - tenant_id            (superuser only)
 *  - process_type
 *  - status
 *  - anonymization_group_id
 *  - created_at__gte
 *  - created_at__lte
 *  - search
 *  - ordering
 */
export const getAnonymizationRuns = async ({
  tenantId = null,                 // superadmin optional
  processType = null,
  status = null,
  groupId = null,
  createdAtGte = null,             // e.g. "2026-02-01" or ISO datetime
  createdAtLte = null,
  search = null,
  ordering = null,                 // e.g. "-created_at" / "pages"
  page = null,                     // if pagination enabled in DRF
  pageSize = null,                 // if pagination enabled in DRF
} = {}) => {
  const url = buildUrl(`${BASE_PATH}/`, {
    tenant_id: tenantId,
    process_type: processType,
    status,
    anonymization_group_id: groupId,
    created_at__gte: createdAtGte,
    created_at__lte: createdAtLte,
    search,
    ordering,
    page,
    page_size: pageSize,
  });

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to load runs (status ${response.status}): ${text}`);
  }

  return await response.json();
};

/**
 * Get a single run detail
 */
export const getAnonymizationRunDetail = async (id, { tenantId = null } = {}) => {
  const url = buildUrl(`${BASE_PATH}/${id}/`, {
    tenant_id: tenantId, // superadmin optional; safe to include
  });

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to load run (status ${response.status}): ${text}`);
  }

  return await response.json();
};

/**
 * Download the original checked document of a run (for preview).
 *
 * GET /anonymization/runs/<id>/document/
 * Returns { blob, filename, contentType }.
 * Backend returns 404 when the document was not stored or its retention expired.
 */
export const getAnonymizationRunDocument = async (id, { tenantId = null } = {}) => {
  const url = buildUrl(`${BASE_PATH}/${id}/document/`, {
    tenant_id: tenantId,
  });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    },
  });

  if (!response.ok) {
    let detail = '';
    try {
      const data = await response.json();
      detail = data?.detail || '';
    } catch {
      detail = '';
    }
    throw new Error(detail || `Dokument sa nepodarilo načítať (stav ${response.status}).`);
  }

  const blob = await response.blob();

  let filename = '';
  const encoded = response.headers.get('X-Document-Filename');
  if (encoded) {
    try {
      filename = decodeURIComponent(encoded);
    } catch {
      filename = encoded;
    }
  }

  return {
    blob,
    filename,
    contentType: response.headers.get('Content-Type') || blob.type || '',
  };
};

/**
 * Fetch aggregated tenant stats from:
 *   GET /anonymization/runs/stats/?...
 *
 * Returns:
 * {
 *   totals: {...},
 *   by_status: [{status, count}, ...],
 *   by_process_type: [{process_type, count, pages, anonymized, input_bytes, output_bytes}, ...]
 * }
 */
export const getAnonymizationRunStats = async ({
  tenantId = null,        // superadmin optional
  processType = null,
  status = null,
  groupId = null,
  createdAtGte = null,
  createdAtLte = null,
  search = null,
} = {}) => {
  const url = buildUrl(`${BASE_PATH}/stats/`, {
    tenant_id: tenantId,
    process_type: processType,
    status,
    anonymization_group_id: groupId,
    created_at__gte: createdAtGte,
    created_at__lte: createdAtLte,
    search,
  });

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to load run stats (status ${response.status}): ${text}`);
  }

  return await response.json();
};